import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppShell } from "@/components/layout/AppShell";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import {
  fetchBootstrap,
  fetchProperties,
  setDefaultProperty,
  type PropertySummary,
} from "@/lib/api";
import { pushGtmEvent } from "@/lib/gtm-events";
import { usePageMeta } from "@/hooks/usePageMeta";

export function PropertyPickerPage() {
  usePageMeta({ title: "Pick a property · Traffic Snapshot" });
  const navigate = useNavigate();
  const [properties, setProperties] = useState<PropertySummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  const [selecting, setSelecting] = useState<string | null>(null);
  // M13.7 — captured on mount so the property_switched event can carry
  // the previous default. Optional: a user landing here for the first
  // time (no default yet) leaves this null.
  const [previousDefaultId, setPreviousDefaultId] = useState<string | null>(null);

  useEffect(() => {
    fetchProperties()
      .then(setProperties)
      .catch((e: Error) => setError(e.message));
    fetchBootstrap()
      .then((b) => setPreviousDefaultId(b.defaultProperty?.propertyId ?? null))
      .catch(() => undefined);
  }, []);

  const filtered = useMemo(() => {
    if (!properties) return [];
    const q = filter.trim().toLowerCase();
    if (!q) return properties;
    return properties.filter(
      (p) =>
        p.propertyName.toLowerCase().includes(q) ||
        (p.accountName ?? "").toLowerCase().includes(q) ||
        p.propertyId.includes(q),
    );
  }, [properties, filter]);

  async function choose(p: PropertySummary) {
    setSelecting(p.propertyId);
    try {
      await setDefaultProperty(p);
      pushGtmEvent("property_switched", {
        from_property_id: previousDefaultId,
        to_property_id: p.propertyId,
        to_property_name: p.propertyName,
      });
      // Hard reload so the App-root bootstrap refetches and AppHome renders
      // with the new default property. React Router's soft navigate keeps
      // the stale auth state from App's useEffect.
      window.location.href = "/app";
    } catch (e) {
      setError((e as Error).message);
      setSelecting(null);
    }
  }

  return (
    <AppShell
      eyebrow="Connect"
      title={
        <>
          Pick a <em className="font-serif text-purple-700">property</em>
        </>
      }
      subtitle="Every GA4 property your Google account can access. We'll remember the one you pick."
    >
      {error ? (
        <Alert variant="destructive" className="mb-6">
          <AlertTitle>We couldn't load your properties</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      <div className="relative mb-6 max-w-md">
        <i
          aria-hidden="true"
          className="fa-regular fa-magnifying-glass pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-neutral-400"
        />
        <Input
          type="search"
          placeholder="Filter by name, account, or ID"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="pl-9"
        />
      </div>

      {properties === null && !error ? (
        <div className="flex items-center gap-3 text-sm text-neutral-500">
          <Spinner size="sm" />
          Fetching GA4 account summaries…
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-lg bg-white p-8 text-center ring-1 ring-neutral-950/5">
          <p className="font-display text-base font-semibold text-neutral-950">
            No GA4 properties matched
          </p>
          <p className="mt-2 text-sm text-neutral-600 text-pretty">
            Try a different search, or make sure the Google account you connected has access to a
            GA4 property.
          </p>
        </div>
      ) : (
        <ul
          role="list"
          className="divide-y divide-neutral-950/5 overflow-hidden rounded-xl bg-white ring-1 ring-neutral-950/5"
        >
          {filtered.map((p) => {
            const isCurrent = previousDefaultId === p.propertyId;
            return (
              <li key={`${p.accountId ?? "na"}-${p.propertyId}`}>
                <button
                  type="button"
                  onClick={() => choose(p)}
                  disabled={selecting !== null}
                  aria-current={isCurrent ? "true" : undefined}
                  className={
                    isCurrent
                      ? "flex w-full items-center justify-between gap-4 px-5 py-4 text-left ring-2 ring-inset ring-pink-500 transition-colors hover:bg-pink-50/60 focus-visible:bg-pink-50/60 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-purple-500 disabled:opacity-60"
                      : "flex w-full items-center justify-between gap-4 px-5 py-4 text-left transition-colors hover:bg-neutral-25 focus-visible:bg-neutral-25 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-purple-500 disabled:opacity-60"
                  }
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="truncate font-display text-sm font-semibold text-neutral-950">
                        {p.propertyName}
                      </p>
                      {isCurrent ? (
                        <span className="inline-flex items-center rounded-full bg-pink-500 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                          Current
                        </span>
                      ) : null}
                    </div>
                    <p className="truncate text-sm text-neutral-500">
                      {p.accountName ?? "—"} · ID {p.propertyId}
                    </p>
                  </div>
                  {selecting === p.propertyId ? (
                    <Spinner size="sm" />
                  ) : (
                    <i
                      className={
                        isCurrent
                          ? "fa-solid fa-check text-sm text-pink-500"
                          : "fa-solid fa-arrow-right text-sm text-neutral-400"
                      }
                      aria-hidden="true"
                    />
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <div className="mt-8 flex items-center justify-between gap-4">
        <p className="text-sm text-neutral-500">
          Don't see the property you expect? Make sure you signed in with the Google account that
          has access.
        </p>
        <Button variant="ghost" size="sm" onClick={() => navigate("/app/connect")}>
          Switch account
        </Button>
      </div>
    </AppShell>
  );
}
